
const roundToDecimalPlaces = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || !isFinite(value)) return 0;
  return Math.round(value * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

export const mapCalculatorToBackend = (calculatorState) => {
  return {
    // Basic info
    project_name: calculatorState.estimateName || '',
    notes: calculatorState.estimateDesc || '',
    
    // Location
    country: calculatorState.country || 'United States',
    state: calculatorState.stateProvince || 'California',
    
    // Building
    square_foot: roundToDecimalPlaces(calculatorState.building?.area || 15000, 0), // Round to whole number
    building_levels: calculatorState.building?.levels || 1,
    
    // Phases
    phase_count: calculatorState.phaseCount || 1,
    single_phase: calculatorState.singlePhase || 'final',
    two_phase_combo: calculatorState.twoPhaseCombo || 'rough_final',
    phase: calculatorState.phaseCount === 1 
      ? calculatorState.singlePhase 
      : (calculatorState.phaseCount === 2 ? calculatorState.twoPhaseCombo : 'rough_final_fluff'),
    
    // Project details
    project_type: calculatorState.projectType || 'Corporate',
    selected_model: calculatorState.selectedModel || 'lean',
    
    // Units and currency
    units_type: calculatorState.units || 'ft',
    currency_type: calculatorState.currency || 'USD',
    
    // Labor
    hourly_labor_rate: roundToDecimalPlaces(calculatorState.labor?.rate_per_hour || 45, 2),
    crew_size: calculatorState.labor?.crew_size || 4,
    laborersOnSite: calculatorState.labor?.crew_size || 4,
    
    // Calculated value 
    bid_amount: roundToDecimalPlaces(calculatorState.calculatedBidAmount || 0, 0),
    noOfDaysExpected: roundToDecimalPlaces(calculatorState.calculatedDays || 0, 0),
    
    // Time & Material
    time_material_enabled: calculatorState.manHour?.enabled || false,
    time_material_rate: roundToDecimalPlaces(calculatorState.manHour?.bill_rate_per_hour || 65, 2),
    
    // Davis-Bacon and Union Wage
    davis_checked: calculatorState.davis?.checked || false,
    davis_percentage: calculatorState.davis?.percentage || 30,
    union_checked: calculatorState.union?.checked || false,
    union_percentage: calculatorState.union?.percentage || 70,
    
    // Janitorial
    janitorial_enabled: calculatorState.janitorial?.enabled || false,
    janitorial_monthly_rate: roundToDecimalPlaces(calculatorState.calculatedJanitorialMonthly || 0, 2),
    
    // Add-ons - Use calculated values (include selected model margin)
    window_panes: roundToDecimalPlaces(
      calculatorState.calculatedWindowPanes || 
      calculatorState.windows?.panes || 
      0, 2
    ),
    price_per_window: roundToDecimalPlaces(
      calculatorState.calculatedWindowPricePerPane || 
      calculatorState.windows?.price_per_pane || 
      0, 4
    ),
    pressure_wash: roundToDecimalPlaces(calculatorState.pressure?.area || 0, 0), // Round to whole number
    pressure_wash_price: roundToDecimalPlaces(
      calculatorState.calculatedPressurePrice ||
      calculatorState.pressure?.rate_per_unit || 
      0, 4
    ),
    
    // Complete state as JSON
    calculator_data_json: JSON.stringify(calculatorState),
  };
};

/**
 * Maps backend API response to frontend calculator state
 * @param {Object} backendData - Data from backend API
 * @param {Object} user - User object
 * @returns {Object} - Frontend Redux state structure
 */
export const mapBackendToCalculator = (backendData, user) => {
  // If we have complete JSON state, parse and return it
  if (backendData.calculator_data_json) {
    try {
      let calculatorState = JSON.parse(backendData.calculator_data_json);
      return {
        ...calculatorState,
        nationalBaseSqft: !localStorage.getItem(`last_national_base_${user?.id}`) && calculatorState.nationalBaseSqft === 15000 ? 0.26 : localStorage.getItem(`last_national_base_${user?.id}`) && calculatorState.nationalBaseSqft === 15000 ?  parseFloat(localStorage.getItem(`last_national_base_${user?.id}`)) : calculatorState.nationalBaseSqft === 15000 ? 0.26 : calculatorState.nationalBaseSqft,
      };
    } catch (error) {
      console.error('Error parsing calculator_data_json:', error);
      // Fall through to field-by-field mapping
    }
  }
  
  // Parse phase string to determine phase configuration
  const phaseString = backendData.phase || 'final';
  const phases = phaseString.split('_');
  const phaseCount = phases.length;
  
  // Determine active phases
  const activePhases = {
    rough: phases.includes('rough'),
    final: phases.includes('final'),
    fluff: phases.includes('fluff')
  };
  
  // Calculate weights based on phase combination
  let weights = { rough: 0, final: 0, fluff: 0 };
  if (phaseCount === 1) {
    weights[phases[0]] = 100;
  } else if (phaseCount === 2) {
    if (phaseString === 'rough_final') {
      weights = { rough: 35, final: 65, fluff: 0 };
    } else if (phaseString === 'final_fluff') {
      weights = { rough: 0, final: 70, fluff: 30 };
    } else if (phaseString === 'rough_fluff') {
      weights = { rough: 60, final: 0, fluff: 40 };
    }
  } else if (phaseCount === 3) {
    weights = { rough: 25, final: 50, fluff: 25 };
  }
  
  // Build calculator state from individual fields
  return {
    // Basic info
    estimateName: backendData.project_name || '',
    estimateDesc: backendData.notes || '',
    
    // Location
    country: backendData.country || 'United States',
    stateProvince: backendData.state || 'California',
    
    // Building
    building: {
      area: parseFloat(backendData.square_foot) || 15000,
      levels: parseInt(backendData.building_levels) || 1,
      useDetailed: false,
      autoArea: true,
      details: [
        { name: 'Bldg A', area: 0 },
        { name: 'Bldg B', area: 0 },
        { name: 'Bldg C', area: 0 }
      ],
    },
    
    // Phases
    phaseCount: phaseCount,
    singlePhase: phaseCount === 1 ? phases[0] : (backendData.single_phase || 'final'),
    twoPhaseCombo: phaseCount === 2 ? phaseString : (backendData.two_phase_combo || 'rough_final'),
    activePhases: activePhases,
    weights: weights,
    phasePasses: { rough: 1, final: 1, fluff: 1 },
    
    // Project details
    projectType: backendData.project_type || 'Corporate',
    selectedModel: backendData.selected_model || 'lean',
    
    // Units and currency
    units: backendData.units_type || 'ft',
    currency: backendData.currency_type || 'USD',
    
    // Labor
    labor: {
      rate_per_hour: parseFloat(backendData.hourly_labor_rate) || 45,
      crew_size: parseInt(backendData.crew_size) || 4,
    },
    autoLabor: true,
    
    // Time & Material
    manHour: {
      enabled: backendData.time_material_enabled || false,
      bill_rate_per_hour: parseFloat(backendData.time_material_rate) || 65,
      apply_to_selected: false,
      override: false,
      manual_hours: 0,
    },
    
    // Davis-Bacon and Union Wage
    davis: {
      checked: backendData.davis_checked || false,
      percentage: parseInt(backendData.davis_percentage) || 30,
    },
    union: {
      checked: backendData.union_checked || false,
      percentage: parseInt(backendData.union_percentage) || 70,
    },
    
    // Janitorial
    janitorial: {
      enabled: backendData.janitorial_enabled || false,
      addToBid: false,
      visits_per_day: 1,
      days_per_week: 5,
      weeks_per_month: 4.33,
      scope: 'standard',
      apply_to_selected: false,
      apply_basis: 'monthly',
      preset: '',
      supplies_pct: 5,
    },
    
    // Windows
    windows: {
      enabled: true,
      addToBid: false,
      mode: 'auto',
      panes: parseFloat(backendData.window_panes) || 0,
      price_per_pane: parseFloat(backendData.price_per_window) || 5,
      density_per_1000ft2: 4,
      include_lift: false,
      lift_cost_daily: 0,
      lift_days: 0,
      units_count: 0,
      panes_per_unit: 12,
    },
    
    // Pressure wash
    pressure: {
      enabled: true,
      addToBid: false,
      area: parseFloat(backendData.pressure_wash) || 0,
      rate_per_unit: parseFloat(backendData.pressure_wash_price) || 0.2,
      preset: 'manual',
    },
    
    // Flooring (defaults if not in backend)
    flooring: {
      enabled: true,
      addToBid: false,
      area: 0,
      coats: 1,
      price_per_coat: 0.14,
    },
    
    // Units/Baths (defaults if not in backend)
    unitsBaths: {
      enabled: true,
      addToBid: false,
      units: 0,
      base_rate_per_unit: 0,
      rate_per_unit: 0,
      baths: 0,
      base_rate_per_bath: 0,
      rate_per_bath: 0,
      userEdited: false
    },
    
    // Units Mix (defaults if not in backend)
    unitsMix: {
      enabled: true,
      addToBid: false,
      userEdited: false,
      rows: [
        { type: 'Studio', baseRate: 200, ratePerBath: 50, baseRateUnscaled: 200, ratePerBathUnscaled: 50, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 500 },
        { type: '1 BR', baseRate: 250, ratePerBath: 55, baseRateUnscaled: 250, ratePerBathUnscaled: 55, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 750 },
        { type: '2 BR', baseRate: 300, ratePerBath: 60, baseRateUnscaled: 300, ratePerBathUnscaled: 60, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1000 },
        { type: '3 BR', baseRate: 350, ratePerBath: 65, baseRateUnscaled: 350, ratePerBathUnscaled: 65, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1250 },
        { type: '4 BR', baseRate: 400, ratePerBath: 70, baseRateUnscaled: 400, ratePerBathUnscaled: 70, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1500 },
        { type: '5 BR', baseRate: 450, ratePerBath: 75, baseRateUnscaled: 450, ratePerBathUnscaled: 75, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1750 },
      ]
    },
    
    // Travel (defaults if not in backend)
    travel: {
      enabled: true,
      addToBid: false,
      auto: true,
      visits: 0,
      cost_per_visit: 0,
    },
    
    // Collapse states (defaults)
    collapsed: {
      project: false,
      phases: true,
      pricing: false,
      ops: true,
      unitsBaths: true,
      unitsMix: true,
      windows: true,
      pressure: true,
      flooring: true,
      janitorial: true,
      proposalExport: true
    },
    
    // Show phase breakout
    showPhaseBreakout: true,
  };
};

export const getEstimateCalculatorVersion = (backendData) => {
  if (!backendData) return "new";

  const rawJson = backendData.calculator_data_json;
  if (!rawJson || rawJson === "null" || rawJson === "{}") {
    return "legacy";
  }

  try {
    const parsed = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
    if (
      parsed &&
      typeof parsed === "object" &&
      (parsed.estimateName !== undefined ||
        parsed.building !== undefined ||
        parsed.selectedModel !== undefined ||
        parsed.stateProvince !== undefined)
    ) {
      return "new";
    }
  } catch (error) {
    console.error("Error detecting calculator version:", error);
  }

  return "legacy";
};

export const syncNewToLegacyState = (calculatorState) => {
  return mapCalculatorToBackend(calculatorState);
};

export const syncLegacyToNewState = (bidAmountValues, costProfitValues, user) => {
  const backendPayload = {
    ...costProfitValues,
    ...bidAmountValues,
  };
  return mapBackendToCalculator(backendPayload, user);
};
